<template>
  <!-- Teleported, because `position: fixed` resolves against the nearest
       transformed ancestor and this site's pages carry a permanently
       translated track. -->
  <Teleport to="body">
    <Transition name="confirm-fade">
      <div v-if="open" class="confirm" @click.self="answer(false)">
        <section
          ref="box"
          class="confirm__box"
          role="alertdialog"
          aria-modal="true"
          :aria-labelledby="titleId"
        >
          <h2 :id="titleId" class="confirm__title">{{ options.title }}</h2>
          <p v-if="options.note" class="confirm__note">{{ options.note }}</p>

          <div class="confirm__row">
            <!-- The safe answer first and focused. This dialog follows a
                 mis-tap at least as often as a decision, so the way out is the
                 one already under the thumb and under the return key. -->
            <button ref="safe" type="button" class="confirm__cancel" @click="answer(false)">
              {{ options.cancelLabel }}
            </button>
            <button
              type="button"
              class="confirm__go"
              :class="{ 'is-danger': options.danger }"
              @click="answer(true)"
            >
              {{ options.confirmLabel }}
            </button>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
import { answer, useConfirmState } from '~/composables/useConfirm';

const { open, options } = useConfirmState();
const titleId = useId();
const safe = ref(null);

// Escape answers no, wherever the focus happens to be.
function onKey(event) {
  if (!open.value) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    answer(false);
  }
}

watch(open, async (isOpen) => {
  if (!isOpen) return;
  await nextTick();
  safe.value?.focus();
});

onMounted(() => document.addEventListener('keydown', onKey));
onBeforeUnmount(() => document.removeEventListener('keydown', onKey));
</script>

<style lang="scss" scoped>
.confirm {
  position: fixed;
  inset: 0;
  // Above the editor overlays it is usually asked from, so the work stays
  // visible behind the question rather than being replaced by it.
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(8, 12, 18, 0.55);
}

.confirm__box {
  width: min(24rem, 100%);
  padding: 1.25rem;
  border-radius: 14px;
  border: 1px solid rgba(var(--border-rgb), 0.5);
  background: var(--background);
  color: var(--text);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.28);
}

.confirm__title {
  margin: 0 0 0.35rem;
  font-size: 1rem;
  line-height: 1.3;
}

.confirm__note {
  margin: 0 0 1.1rem;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--muted);
}

.confirm__row {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.confirm__cancel,
.confirm__go {
  padding: 0.45rem 1rem;
  border-radius: 999px;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.confirm__cancel {
  border: 1px solid rgba(var(--border-rgb), 0.45);
  background: transparent;
  color: var(--secondary);
}

.confirm__go {
  border: none;
  background: var(--accent);
  color: #fff;

  // Destructive, and coloured like it rather than left to the copy alone.
  &.is-danger {
    background: #b91c1c;
  }
}

.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.16s ease-in-out;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .confirm-fade-enter-active,
  .confirm-fade-leave-active {
    transition: none;
  }
}
</style>
